import * as stylex from '@stylexjs/stylex';
import { createFileRoute, redirect } from '@tanstack/react-router';
import type { ReactElement } from 'react';

import {
  Breadcrumb,
  Breadcrumbs,
  Heading,
  Paragraph,
  View,
} from '@/libs/serenity-ui';
import { $ } from '@/libs/serenity-ui/tokens.stylex';
import { RouterLink } from '@/libs/tanstack-router';

export const Route = createFileRoute('/_main/_details/campaigns/edit/guide/')({
  component: RouteComponent,
  beforeLoad: ({ context }) => {
    if (context.user && !context.can.campaignRequests.view()) {
      throw redirect({ to: '/' });
    }
  },
  staticData: {
    details: {
      heading: 'Edit campaigns',
      slots: {
        topBarComponent: TopBarComponent,
      },
    },
  },
  head: () => ({
    meta: [
      {
        title: 'TrendMD | Campaigns — Edit campaigns',
      },
    ],
  }),
});

function RouteComponent(): ReactElement {
  return (
    <article {...stylex.props(articleStyles.base)}>
      <View style={introSectionStyles.base}>
        <View style={introListStyles.wrapper}>
          <Paragraph color="secondary" size={16} style={introListStyles.lead}>
            Depending on the status of a campaign, you can edit campaigns as follows:
          </Paragraph>
          <ul {...stylex.props(introListStyles.base)}>
            <li {...stylex.props(introListStyles.item)}>
              Edit directly in the following statuses:
              <ul {...stylex.props(introListStyles.nested)}>
                <li {...stylex.props(introListStyles.nestedItem)}>Draft</li>
                <li {...stylex.props(introListStyles.nestedItem)}>In review</li>
              </ul>
            </li>
            <li {...stylex.props(introListStyles.item)}>
              Request edits in the following statuses:
              <ul {...stylex.props(introListStyles.nested)}>
                <li {...stylex.props(introListStyles.nestedItem)}>Active</li>
                <li {...stylex.props(introListStyles.nestedItem)}>Completed</li>
              </ul>
            </li>
          </ul>
        </View>
      </View>

      <section {...stylex.props(sectionStyles.base)}>
        <Heading level={2} size={24} weight="medium">Edit campaign drafts</Heading>
        <ol {...stylex.props(orderedListStyles.base)}>
          <li {...stylex.props(orderedListStyles.item)}>
            In the dashboard, go to <strong {...stylex.props(textStyles.primary)}>Campaigns</strong>.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            Click the name of the campaign you want to edit.
            <br />
            This opens the <strong {...stylex.props(textStyles.primary)}>Edit</strong> page.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>Update the needed fields.</li>
          <li {...stylex.props(orderedListStyles.item)}>
            Click one of the following to finalize:
            <ul {...stylex.props(nestedListStyles.base)}>
              <li {...stylex.props(nestedListStyles.item)}>
                <strong {...stylex.props(textStyles.primary)}>Save</strong>
                <span {...stylex.props(nestedListStyles.description)}>
                  : Save the campaign as a draft to come back to later.
                </span>
              </li>
              <li {...stylex.props(nestedListStyles.item)}>
                <strong {...stylex.props(textStyles.primary)}>Submit</strong>
                <span {...stylex.props(nestedListStyles.description)}>
                  : Submit the campaign for review. You will get a notification when the campaign gets approved. If
                  changes or additional information is needed, you&apos;ll get a message.
                </span>
              </li>
            </ul>
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            This shows a success message and updates the campaign.
          </li>
        </ol>
      </section>

      <section {...stylex.props(sectionStyles.base)}>
        <Heading level={2} size={24} weight="medium">Edit campaigns in review</Heading>
        <ol {...stylex.props(orderedListStyles.base)}>
          <li {...stylex.props(orderedListStyles.item)}>
            In the dashboard, go to <strong {...stylex.props(textStyles.primary)}>Campaigns</strong>.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            Click the name of the campaign you want to edit.
            <br />
            This opens the campaign details page.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            In the top-right corner click the context menu and select{' '}
            <strong {...stylex.props(textStyles.primary)}>Edit</strong>.
            <br />
            This opens the <strong {...stylex.props(textStyles.primary)}>Edit</strong> page.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>Update the needed fields.</li>
          <li {...stylex.props(orderedListStyles.item)}>
            Click <strong {...stylex.props(textStyles.primary)}>Submit</strong>.
          </li>
        </ol>
        <Paragraph color="secondary" size={16}>
          This shows a success message and updates the campaign.
        </Paragraph>
      </section>

      <section {...stylex.props(sectionStyles.base)}>
        <Heading level={2} size={24} weight="medium">Edit active and completed campaigns</Heading>
        <ol {...stylex.props(orderedListStyles.base)}>
          <li {...stylex.props(orderedListStyles.item)}>
            In the dashboard, go to <strong {...stylex.props(textStyles.primary)}>Campaigns</strong>.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            Click the name of the campaign you want to edit.
            <br />
            This opens the campaign details page.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            In the top-right corner click the context menu and select{' '}
            <strong {...stylex.props(textStyles.primary)}>Request edit</strong>.
            <br />
            This opens the <strong {...stylex.props(textStyles.primary)}>Request edit</strong> page.
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            Update any of the following fields:
            <ul {...stylex.props(nestedListStyles.base)}>
              <li {...stylex.props(nestedListStyles.item)}>
                 For <strong {...stylex.props(textStyles.primary)}>Budget adjustment</strong>, enter the amount you want
                 to add to or subtract from the campaign budget. Use a negative value to reduce the budget.
                <br />
                 This displays the new total budget after the adjustment.
              </li>
              <li {...stylex.props(nestedListStyles.item)}>
                For <strong {...stylex.props(textStyles.primary)}>End date</strong>, enter the new end date for the
                campaign.
              </li>
              <li {...stylex.props(nestedListStyles.item)}>
                To pause the campaign, click <strong {...stylex.props(textStyles.primary)}>Pause</strong>, then enter a{' '}
                <strong {...stylex.props(textStyles.primary)}>Reason</strong>.
              </li>
            </ul>
          </li>
          <li {...stylex.props(orderedListStyles.item)}>
            Click <strong {...stylex.props(textStyles.primary)}>Submit</strong>.
            <br />
            This shows a success message and displays the request in the{' '}
            <strong {...stylex.props(textStyles.primary)}>Edit requests</strong> table.
            <br />
            Once the request is processed, you&apos;ll get a notification.
          </li>
        </ol>
      </section>
    </article>
  );
}

function TopBarComponent(): ReactElement {
  return (
    <Breadcrumbs>
      <Breadcrumb>
        <RouterLink to="/campaigns">Campaigns</RouterLink>
      </Breadcrumb>
      <Breadcrumb hidden>
        <RouterLink to="/campaigns/edit/guide">Edit campaigns</RouterLink>
      </Breadcrumb>
    </Breadcrumbs>
  );
}

const articleStyles = stylex.create({
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 88,
    justifySelf: 'center',
    maxWidth: 728,
    minWidth: 0,
    width: '100%',
  },
});

const introSectionStyles = stylex.create({
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 32,
  },
});

const introListStyles = stylex.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: 0,
  },
  lead: {
    marginBottom: 18,
  },
  base: {
    color: $.textSecondary,
    fontFamily: 'Inter',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontSize: 16,
    fontWeight: 400,
    letterSpacing: -0.15,
    lineHeight: '28px',
    margin: 0,
    paddingLeft: 20,
  },
  item: {
    marginBottom: 0,
  },
  nested: {
    marginBlock: 0,
    paddingLeft: 20,
  },
  nestedItem: {
    color: $.textSecondary,
    fontFamily: 'Inter',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontSize: 16,
    fontWeight: 400,
    letterSpacing: -0.15,
    lineHeight: '28px',
    listStyleType: 'disc',
  },
});

const sectionStyles = stylex.create({
  base: {
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
  },
});

const orderedListStyles = stylex.create({
  base: {
    color: $.textSecondary,
    fontFamily: 'Inter',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontSize: 16,
    fontWeight: 400,
    letterSpacing: -0.15,
    lineHeight: '28px',
    margin: 0,
    paddingLeft: 20,
  },
  item: {
    marginBottom: 0,
  },
});

const nestedListStyles = stylex.create({
  base: {
    marginBlock: 0,
    paddingLeft: 20,
  },
  item: {
    color: $.textSecondary,
    fontFamily: 'Inter',
    fontFeatureSettings: "'liga' off, 'clig' off",
    fontSize: 16,
    fontWeight: 400,
    letterSpacing: -0.15,
    lineHeight: '28px',
    listStyleType: 'disc',
  },
  description: {
    color: $.textSecondary,
    fontWeight: 400,
  },
});

const textStyles = stylex.create({
  primary: {
    color: $.textPrimary,
    fontWeight: 600,
  },
});
